import { supabase } from './supabase';

export type BookingHelperIssueType = 'stuck_in_progress' | 'overdue_scheduled';

export interface BookingHelperIssue {
  bookingId: string;
  customerName: string;
  title: string | null;
  bookingNumber: string | null;
  startDate: string | null;
  endDate: string | null;
  status: string;
  issueType: BookingHelperIssueType;
  /** "Today" in the business timezone, as computed server-side (yyyy-MM-dd). */
  businessToday: string;
}

interface RawBookingHelperIssue {
  booking_id: string;
  customer_name: string;
  title: string | null;
  booking_number: string | null;
  start_date: string | null;
  end_date: string | null;
  status: string;
  issue_type: BookingHelperIssueType;
  business_today: string;
}

/**
 * Fetch bookings that are out of sync with today's date (in the business
 * timezone). Detection runs entirely server-side in get_booking_helper_issues.
 */
export async function getBookingHelperIssues(businessId: string): Promise<BookingHelperIssue[]> {
  const { data, error } = await supabase.rpc('get_booking_helper_issues', {
    p_business_id: businessId,
  });

  if (error) throw error;

  return ((data ?? []) as RawBookingHelperIssue[]).map((row) => ({
    bookingId: row.booking_id,
    customerName: row.customer_name,
    title: row.title,
    bookingNumber: row.booking_number,
    startDate: row.start_date,
    endDate: row.end_date,
    status: row.status,
    issueType: row.issue_type,
    businessToday: row.business_today,
  }));
}

/** Extend an in-progress booking's end date to today (keeps end_time as-is). */
export async function extendBookingToToday(bookingId: string, today: string): Promise<void> {
  const { error } = await supabase
    .from('bookings')
    .update({ end_date: today })
    .eq('id', bookingId);
  if (error) throw error;
}

export async function markBookingCompleted(bookingId: string): Promise<void> {
  // Enforce required booking documents — the Booking Helper must not bypass the gate.
  // (Admins can still complete with an override from the booking's own completion flow.)
  try {
    const { data: bk } = await supabase.from('bookings').select('business_id').eq('id', bookingId).maybeSingle();
    if (bk?.business_id) {
      const { data: reqs } = await supabase.rpc('get_booking_document_requirements', { p_business_id: bk.business_id, p_booking_id: bookingId });
      const incomplete = ((reqs as any[]) || []).filter((r) => r.enforcement === 'required' && !r.completed);
      if (incomplete.length > 0) {
        throw new Error('This booking has required documents that must be completed first. Open the booking to complete or override them.');
      }
    }
  } catch (e) {
    // Only swallow lookup errors, not our explicit gate error.
    if (e instanceof Error && e.message.includes('required documents')) throw e;
    console.error('[BookingHelper] doc requirement check failed:', e);
  }

  const { error } = await supabase
    .from('bookings')
    .update({ status: 'completed', completed_at: new Date().toISOString() })
    .eq('id', bookingId);
  if (error) throw error;
}

/**
 * Which of these bookings already have money on them (counted payments with a
 * positive amount)? Used to decide whether "Mark completed & paid" is offered.
 */
export async function getBookingIdsWithPayments(bookingIds: string[]): Promise<Set<string>> {
  if (bookingIds.length === 0) return new Set();
  const { data, error } = await supabase
    .from('payments')
    .select('booking_id, amount, status')
    .in('booking_id', bookingIds)
    .in('status', ['completed', 'succeeded', 'paid']);
  if (error) throw error;
  return new Set(
    ((data ?? []) as { booking_id: string; amount: number | null }[])
      .filter((p) => (Number(p.amount) || 0) > 0)
      .map((p) => p.booking_id),
  );
}

/**
 * Mark a booking completed AND fully paid: completes the booking (issuing the
 * invoice), then records a payment for the remaining balance. The payment
 * triggers allocate it to the invoice, whose rollup flips it to "paid".
 */
export async function markBookingCompletedAndPaid(bookingId: string): Promise<void> {
  // Completing first issues/locks the invoice with final totals.
  await markBookingCompleted(bookingId);

  const { data: booking, error: bookingErr } = await supabase
    .from('bookings')
    .select('business_id, total_amount')
    .eq('id', bookingId)
    .single();
  if (bookingErr || !booking) throw bookingErr || new Error('Booking not found');

  // Remaining balance = total − counted payments already on the booking.
  const { data: payments } = await supabase
    .from('payments')
    .select('amount, surcharge_amount, tip_amount, status')
    .eq('booking_id', bookingId)
    .in('status', ['completed', 'succeeded', 'paid']);
  const paid = ((payments ?? []) as { amount: number | null; surcharge_amount: number | null; tip_amount: number | null }[])
    .reduce((sum, p) => sum + Math.max(0, (Number(p.amount) || 0) - (Number(p.surcharge_amount) || 0) - (Number(p.tip_amount) || 0)), 0);
  const remaining = Math.round(Math.max(0, (Number(booking.total_amount) || 0) - paid) * 100) / 100;
  if (remaining <= 0) return; // nothing owing — completing was enough

  const { error: payErr } = await supabase.from('payments').insert({
    business_id: booking.business_id,
    booking_id: bookingId,
    amount: remaining,
    base_amount: remaining,
    surcharge_amount: 0,
    tip_amount: 0,
    method: 'other',
    status: 'completed',
    payment_type: 'final',
    notes: 'Marked as paid from Keep Bookings Current',
  });
  if (payErr) throw payErr;
}

export async function markBookingInProgress(bookingId: string): Promise<void> {
  const { error } = await supabase
    .from('bookings')
    .update({ status: 'in_progress' })
    .eq('id', bookingId);
  if (error) throw error;
}

/** Cancel (never delete) a booking by setting its status to cancelled. */
export async function cancelBooking(bookingId: string): Promise<void> {
  const { error } = await supabase
    .from('bookings')
    .update({ status: 'cancelled' })
    .eq('id', bookingId);
  if (error) throw error;
}

/**
 * Unschedule a booking: clear its dates and move it to the calendar's
 * unscheduled bucket (same shape as bookings created without a date), so it
 * can be rebooked later from the calendar.
 */
export async function unscheduleBooking(bookingId: string): Promise<void> {
  const { error } = await supabase
    .from('bookings')
    .update({
      is_unscheduled: true,
      status: 'scheduled',
      start_date: null,
      start_time: null,
      end_date: null,
      end_time: null,
      drop_off_date: null,
      drop_off_time: null,
    })
    .eq('id', bookingId);
  if (error) throw error;
}

/**
 * Soft-delete a booking (is_deleted = true) — the existing deletion triggers
 * handle downstream cleanup (e.g. draft invoice removal / voiding).
 */
export async function deleteBookingSoft(bookingId: string): Promise<void> {
  const { error } = await supabase
    .from('bookings')
    .update({ is_deleted: true })
    .eq('id', bookingId);
  if (error) throw error;
}

/** Permanently ignore a booking so the helper never flags it again. */
export async function ignoreBookingHelper(bookingId: string): Promise<void> {
  const { error } = await supabase
    .from('bookings')
    .update({ booking_helper_ignored: true })
    .eq('id', bookingId);
  if (error) throw error;
}
